import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Search, User, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PaginationControls } from '@/components/pagination-controls';
import { usePagination } from '@/hooks/use-pagination';
import { paginateArray } from '@/types/pagination';
import { fetchRolesWithRates, fetchEmployeesByRoleId } from '@/lib/data-service';
import type { RoleWithRates, RoleEmployee } from '@/lib/data-service';
import { useDataMode } from '@/contexts/DataModeContext';

export function RolesRatesPage() {
  const { mode } = useDataMode();
  const [searchTerm, setSearchTerm] = useState('');
  const [roles, setRoles] = useState<RoleWithRates[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState<RoleWithRates | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [roleEmployees, setRoleEmployees] = useState<RoleEmployee[]>([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);

  const {
    currentPage,
    pageSize,
    setPage,
    setPageSize,
    resetPagination,
  } = usePagination({ defaultPageSize: 25, persistInUrl: false });

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    fetchRolesWithRates()
      .then((data) => {
        if (!cancelled) setRoles(data);
      })
      .catch((err) => {
        console.error('Failed to fetch roles:', err);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [mode]);

  const filteredRoles = useMemo(() => {
    return roles.filter(
      (role) =>
        role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (role.description && role.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [searchTerm, roles]);

  React.useEffect(() => {
    resetPagination();
  }, [searchTerm, resetPagination]);

  const paginatedResult = useMemo(() => {
    return paginateArray(filteredRoles, currentPage, pageSize);
  }, [filteredRoles, currentPage, pageSize]);

  const paginatedRoles = paginatedResult.data;
  const pagination = paginatedResult.pagination;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const calculateMargin = (costRate: number, billRate: number) => {
    if (billRate === 0) return '0';
    return ((billRate - costRate) / billRate * 100).toFixed(0);
  };

  const getMarginVariant = (margin: number) => {
    if (margin >= 60) return 'default';
    if (margin >= 40) return 'secondary';
    return 'outline';
  };

  const handleRowClick = useCallback((role: RoleWithRates) => {
    setSelectedRole(role);
    setIsSheetOpen(true);
    setIsLoadingEmployees(true);
    setRoleEmployees([]);
    fetchEmployeesByRoleId(role.id)
      .then((data) => {
        setRoleEmployees(data);
      })
      .catch((err) => {
        console.error('Failed to fetch employees for role:', err);
      })
      .finally(() => {
        setIsLoadingEmployees(false);
      });
  }, []);

  const getDailyRate = (hourlyRate: number, dailyRate?: number) => {
    return dailyRate ?? hourlyRate * 8;
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Roles & Rates</h1>
        <p className="text-muted-foreground mt-2">
          View and compare role rates across your organization
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search roles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">Role</TableHead>
                  <TableHead className="w-[280px]">Description</TableHead>
                  <TableHead className="text-right w-[140px]">
                    <div className="flex flex-col items-end">
                      <span>Cost Rate</span>
                      <span>(Hour)</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-right w-[140px]">
                    <div className="flex flex-col items-end">
                      <span>Cost Rate</span>
                      <span>(Day)</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-right w-[140px]">
                    <div className="flex flex-col items-end">
                      <span>Bill Rate</span>
                      <span>(Hour)</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-right w-[140px]">
                    <div className="flex flex-col items-end">
                      <span>Bill Rate</span>
                      <span>(Day)</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-right w-[100px]">Margin</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Loading roles...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : paginatedRoles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No roles found matching your search.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRoles.map((role) => {
                    const margin = parseFloat(calculateMargin(role.defaultCostRate, role.defaultBillRate));
                    const dailyCost = getDailyRate(role.defaultCostRate, role.defaultDailyCostRate);
                    const dailyBill = getDailyRate(role.defaultBillRate, role.defaultDailyBillRate);
                    return (
                      <TableRow 
                        key={role.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleRowClick(role)}
                      >
                        <TableCell className="font-medium">{role.name}</TableCell>
                        <TableCell>
                          <div className="max-w-[280px] truncate" title={role.description}>
                            {role.description}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(role.defaultCostRate)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(dailyCost)}
                        </TableCell>
                        <TableCell className="text-right text-green-600">
                          {formatCurrency(role.defaultBillRate)}
                        </TableCell>
                        <TableCell className="text-right text-green-600">
                          {formatCurrency(dailyBill)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant={getMarginVariant(margin)}>
                            {margin}%
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
        
        {filteredRoles.length > 0 && (
          <div className="px-6 pb-6">
            <PaginationControls
              pagination={pagination}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              showPageSizeSelector={true}
            />
          </div>
        )}
      </Card>

      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto p-6">
          {selectedRole && (
            <>
              <SheetHeader className="mb-6">
                <SheetTitle>{selectedRole.name}</SheetTitle>
                <SheetDescription>{selectedRole.description}</SheetDescription>
              </SheetHeader>

              <div className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold">Rate Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">Cost Rate (Hour)</p>
                      <p className="text-lg font-semibold">{formatCurrency(selectedRole.defaultCostRate)}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">Bill Rate (Hour)</p>
                      <p className="text-lg font-semibold text-green-600">{formatCurrency(selectedRole.defaultBillRate)}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">Cost Rate (Day)</p>
                      <p className="text-lg font-semibold">{formatCurrency(getDailyRate(selectedRole.defaultCostRate, selectedRole.defaultDailyCostRate))}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">Bill Rate (Day)</p>
                      <p className="text-lg font-semibold text-green-600">{formatCurrency(getDailyRate(selectedRole.defaultBillRate, selectedRole.defaultDailyBillRate))}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold">
                    Allocated Resources ({isLoadingEmployees ? '...' : roleEmployees.length})
                  </h3>
                  
                  {isLoadingEmployees ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <Loader2 className="h-8 w-8 text-muted-foreground mb-2 animate-spin" />
                      <p className="text-sm text-muted-foreground">
                        Loading resources...
                      </p>
                    </div>
                  ) : roleEmployees.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <User className="h-12 w-12 text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">
                        No resources currently allocated to this role
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {roleEmployees.map((employee) => (
                        <Card key={employee.id} className="hover:bg-muted/50 transition-colors">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-4">
                              <Avatar className="h-12 w-12 rounded-md">
                                <AvatarImage src={employee.photo} alt={employee.name} />
                                <AvatarFallback className="rounded-md">
                                  {employee.name.split(' ').map(n => n[0]).join('')}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{employee.name}</p>
                                <p className="text-sm text-muted-foreground truncate">{employee.email}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  <Badge variant="outline" className="text-xs">
                                    {employee.department}
                                  </Badge>
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
